import { surveyApi } from "./base";

export interface TourStop {
  stopId: string;
  surveyId: string;
  propertyId?: string;              // Property association (null for independent stops)
  stopType: "CUSTOM_LOCATION" | "CURRENT_LOCATION";
  label: string;
  address?: string;
  stopDetails?: string;
  order: number;
  active?: boolean;
  createdBy?: string;
  createdOn?: string;
  updatedBy?: string;
  updatedOn?: string;
}

export interface TourStopsResponse {
  surveyId: string;
  stops: TourStop[];
}

export interface CreateTourStopRequest {
  stopType: "CUSTOM_LOCATION" | "CURRENT_LOCATION";
  label: string;
  address?: string;
  stopDetails?: string;
}

export interface UpdateTourStopRequest {
  stopType?: "CUSTOM_LOCATION" | "CURRENT_LOCATION";
  label?: string;
  address?: string;
  stopDetails?: string;
}

export interface ReorderTourStopsRequest {
  stopOrders: Array<{
    stopId: string;
    surveyId: string;
    order: number;
  }>;
}

export interface AttachPropertyRequest {
  propertyId: string;
}

// Tour Stops API - handles custom tour stop management
export const surveyTourStopsApi = surveyApi
  .enhanceEndpoints({
    addTagTypes: ["TourStops"],
  })
  .injectEndpoints({
    endpoints: (build) => ({
      // GET /tour-stops/{survey_id}
      getTourStops: build.query<TourStop[], string>({
        query: (surveyId) => ({
          url: `/tour-stops/${surveyId}`,
          method: "GET",
        }),
        transformResponse: (response: TourStopsResponse) => {
          // Extract stops array from wrapper and filter active stops
          return (response.stops || []).filter(stop => stop.active !== false);
        },
        providesTags: (result, _error, surveyId) =>
          result
            ? [
                ...result.map(({ stopId }) => ({
                  type: "TourStops" as const,
                  id: stopId,
                })),
                { type: "TourStops", id: `SURVEY-${surveyId}` },
              ]
            : [{ type: "TourStops", id: `SURVEY-${surveyId}` }],
      }),

      // GET /tour-stops/{survey_id}/{stop_id}
      getTourStop: build.query<TourStop, { surveyId: string; stopId: string }>(
        {
          query: ({ surveyId, stopId }) => ({
            url: `/tour-stops/${surveyId}/${stopId}`,
            method: "GET",
          }),
          providesTags: (_result, _error, { stopId }) => [
            { type: "TourStops", id: stopId },
          ],
        }
      ),

      // POST /tour-stops/{survey_id}
      createTourStop: build.mutation<
        TourStop,
        { surveyId: string; body: CreateTourStopRequest }
      >({
        query: ({ surveyId, body }) => ({
          url: `/tour-stops/${surveyId}`,
          method: "POST",
          body,
        }),
        invalidatesTags: (_result, _error, { surveyId }) => [
          { type: "TourStops", id: `SURVEY-${surveyId}` },
        ],
      }),

      // PATCH /tour-stops/{survey_id}/{stop_id}
      updateTourStop: build.mutation<
        TourStop,
        { surveyId: string; stopId: string; body: UpdateTourStopRequest }
      >({
        query: ({ surveyId, stopId, body }) => ({
          url: `/tour-stops/${surveyId}/${stopId}`,
          method: "PATCH",
          body,
        }),
        // Optimistic cache update: patch the getTourStops cache immediately so
        // consumers see the new values without waiting for a refetch, and roll
        // back on error. Mirrors property stopDetails behavior and prevents
        // stale reads / list rebuilds mid-edit.
        async onQueryStarted(
          { surveyId, stopId, body },
          { dispatch, queryFulfilled },
        ) {
          const patchResult = dispatch(
            surveyTourStopsApi.util.updateQueryData(
              "getTourStops",
              surveyId,
              (draft) => {
                const stop = draft.find((s) => s.stopId === stopId);
                if (stop) {
                  Object.assign(stop, body);
                }
              },
            ),
          );
          try {
            await queryFulfilled;
          } catch {
            patchResult.undo();
          }
        },
        invalidatesTags: (_result, _error, { stopId, surveyId }) => [
          { type: "TourStops", id: stopId },
          { type: "TourStops", id: `SURVEY-${surveyId}` },
        ],
      }),

      // DELETE /tour-stops/{survey_id}/{stop_id}
      deleteTourStop: build.mutation<
        void,
        { surveyId: string; stopId: string }
      >({
        query: ({ surveyId, stopId }) => ({
          url: `/tour-stops/${surveyId}/${stopId}`,
          method: "DELETE",
        }),
        invalidatesTags: (_result, _error, { surveyId }) => [
          { type: "TourStops", id: `SURVEY-${surveyId}` },
        ],
      }),

      // PATCH /tour-stops/{survey_id}/reorder
      reorderTourStops: build.mutation<
        void,
        { surveyId: string; body: ReorderTourStopsRequest }
      >({
        query: ({ surveyId, body }) => ({
          url: `/tour-stops/${surveyId}/reorder`,
          method: "PATCH",
          body,
        }),
        invalidatesTags: (_result, _error, { surveyId }) => [
          { type: "TourStops", id: `SURVEY-${surveyId}` },
        ],
      }),

      // PATCH /tour-stops/{survey_id}/{stop_id}/attach-property
      attachTourStopToProperty: build.mutation<
        TourStop,
        { surveyId: string; stopId: string; body: AttachPropertyRequest }
      >({
        query: ({ surveyId, stopId, body }) => ({
          url: `/tour-stops/${surveyId}/${stopId}/attach-property`,
          method: "PATCH",
          body,
        }),
        invalidatesTags: (_result, _error, { stopId, surveyId }) => [
          { type: "TourStops", id: stopId },
          { type: "TourStops", id: `SURVEY-${surveyId}` },
        ],
      }),

      // PATCH /tour-stops/{survey_id}/{stop_id}/detach-property
      detachTourStopFromProperty: build.mutation<
        TourStop,
        { surveyId: string; stopId: string }
      >({
        query: ({ surveyId, stopId }) => ({
          url: `/tour-stops/${surveyId}/${stopId}/detach-property`,
          method: "PATCH",
        }),
        invalidatesTags: (_result, _error, { stopId, surveyId }) => [
          { type: "TourStops", id: stopId },
          { type: "TourStops", id: `SURVEY-${surveyId}` },
        ],
      }),
    }),
  });

export const {
  useGetTourStopsQuery,
  useGetTourStopQuery,
  useCreateTourStopMutation,
  useUpdateTourStopMutation,
  useDeleteTourStopMutation,
  useReorderTourStopsMutation,
  useAttachTourStopToPropertyMutation,
  useDetachTourStopFromPropertyMutation,
} = surveyTourStopsApi;
